import {ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AiService} from "../../../services/ai/ai.service";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {LocalStoreService, SceneAddService, StoreSelectorsService, UserService} from "../../../services";
import {
    trigger,
    style,
    animate,
    transition,
    state
} from '@angular/animations';
import {CdkDragEnd} from "@angular/cdk/drag-drop";
import {timeout} from "../../../decorators/timeout";
import {Router} from "@angular/router";
import {delay, switchMap, tap} from "rxjs/operators";
import {of} from "rxjs";
import {aiMapping} from "../../../services/ai/definitions/aiMapping";
import {AiBalanceService} from "../../../services/AiBalanceService";
import {AddAiCreditComponent} from "../../account/ai-billing/add-ai-credit/add-ai-credit.component";
import {AiSettingsDialogComponent} from "../../user/ai-settings/ai-settings-dialog.component";
import {JsonDiffDialogComponent} from "../json-diff-dialog/json-diff-dialog.component";
import {actionCRUDScene, actionUpdateScenePlayer} from "../../../store/actions/user.actions";




@Component({
  selector: 'app-ai-prompt',
  templateUrl: './ai-prompt.component.html',
  styleUrl: './ai-prompt.component.scss',
    animations: [
        trigger('dialogAnim', [
            transition(':enter', [
                style({ opacity: 0, transform: '{{startTransform}}' }),
                animate(
                    '400ms ease-out',
                    style({ opacity: 1, transform: 'translate(0, 0) scale(1)' })
                )
            ], {
                params: { startTransform: 'translate(100%, -100%) scale(0)' } // default value
            }),

            transition(':leave', [
                animate(
                    '400ms ease-in',
                    style({ opacity: 0.6, transform: '{{startTransform}}' })
                )
            ], {
                params: { startTransform: 'translate(100%, -100%) scale(0)' } // default value
            })
        ])
    ]
})
export class AiPromptComponent implements OnInit {
    dialogStartTransform;

    title = '';
    balance;

    private resizing = false;
    private resizeDirection: 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' = 'right';

    aiSettings;
    prompt = '';
    dialogHtml = '';
    dialogContentArray: string[] = [];
    waitMessage = '';
    busy = false;

    // Image download state
    currentImageData: any = null;
    showImageOptions = false;

    showDialog = false;
    isDragging = false;
    offset = {x: 0, y: 0};

    isWaiting = false;
    spinnerFrames = ['|', '/', '-', '\\'];
    currentSpinnerIndex = 0;
    spinnerInterval: any;

    transcript = '';
    recognition: any;
    isListening = false;

    dialogState = {
        top: 200,
        left: 400,
        width: 800,
        height: 600
    };



    private readonly DIALOG_SIZE_KEY = 'ai-prompt-dialog-size';
    private readonly MIN_WIDTH = 500;
    private readonly MIN_HEIGHT = 400;

    @ViewChild('aiDialog') aiDialog!: ElementRef<HTMLElement>;
    @ViewChild('textArea') textArea!: ElementRef<HTMLDivElement>;
    @ViewChild('promptInput') promptInput!: ElementRef<HTMLInputElement>;

    constructor(private dialog: MatDialog,
                public aiService: AiService,
                private userService: UserService,
                private localStoreService: LocalStoreService,
                private storeSelector: StoreSelectorsService,
                private sceneAddService: SceneAddService,
                private router: Router,
                private aiBalanceService: AiBalanceService,
                private cd: ChangeDetectorRef) {

    }

    ngOnInit() {
        // Load saved dialog size from local storage
        this.loadDialogSize();

        this.storeSelector.getLocalStoreUpdates()
            .subscribe((updates) => {
                this.aiSettings = this.localStoreService.getStorageValConfig('ai');
                this.title = this.aiService.aiApis.chat[this.aiSettings.chat].label;
            });

        this.aiBalanceService.balance$.subscribe( balance => {
            this.balance = balance.toFixed(2);
        });


        this.aiService.showDialog$.pipe(
            tap( show => {
                this.updateDialogState();
                const x = this.aiService.x - (this.dialogState.left + this.dialogState.width / 2);
                const y = this.aiService.y - (this.dialogState.top + this.dialogState.height / 2);
                this.dialogStartTransform = `translate(${x}px, ${y}px) scale(0)`;
            }),
            delay(10)
        ).subscribe(show => {


            this.showDialog = show;
        });

        this.aiService.busy$.subscribe(busy => {
            this.busy = busy;
            // Focus on prompt input when AI finishes (busy becomes false)
            if (!busy && this.showDialog && this.promptInput) {
                setTimeout(() => {
                    this.promptInput.nativeElement.focus();
                }, 100);
            }
        });

        this.aiService.log$.subscribe(message => {
            const msg: any = message;
            const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : '';
            const icon = msg.icon || '';

            // Determine if content should be escaped or treated as HTML
            const processContent = (text: string, isHtml: boolean = false) => {
                return isHtml ? text : this.escapeHtml(text);
            };

            let htmlContent = '';

            switch (msg.type) {
                case 'user':
                    htmlContent = `
                        <div class="log-entry log-user">
                            <div class="log-header">
                                <span class="log-icon material-icons">${icon}</span>
                                <span class="log-label">You</span>
                                <span class="log-time">${timestamp}</span>
                            </div>
                            <div class="log-content">${processContent(msg.text, msg.isHtml)}</div>
                        </div>`;
                    break;

                case 'assistant':
                    // Assistant messages may contain HTML formatting
                    htmlContent = `
                        <div class="log-entry log-assistant">
                            <div class="log-header">
                                <span class="log-icon material-icons">${icon}</span>
                                <span class="log-label">Assistant</span>
                                <span class="log-time">${timestamp}</span>
                            </div>
                            <div class="log-content assistant-response">${processContent(msg.text, msg.isHtml)}</div>
                        </div>`;
                    break;

                case 'function':
                    const functionId = `func-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                    // Special handling for 'thinking' - show just the text content
                    let argsDisplay = '';
                    if (msg.args) {
                        if (msg.text === 'thinking' && msg.args.text) {
                            argsDisplay = `<div class="function-args">${processContent(msg.args.text, false)}</div>`;
                        } else {
                            argsDisplay = `<pre class="function-args">${JSON.stringify(msg.args, null, 2)}</pre>`;
                        }
                    }
                    htmlContent = `
                        <div class="log-entry log-function">
                            <div class="log-content">
                                <div class="function-name">
                                    <span class="collapse-toggle" data-function-id="${functionId}" style="user-select: none;">
                                        <span class="arrow material-icons">arrow_right</span>
                                    </span>
                                    <span class="function-text" style="user-select: text;">
                                        ${processContent(msg.text, msg.isHtml)}
                                    </span>
                                    <span class="log-time" style="float: right; color: #808080; font-size: 11px;">${timestamp}</span>
                                </div>
                                <div class="function-args-container collapsed" id="${functionId}">
                                    ${argsDisplay}
                                </div>
                            </div>
                        </div>`;
                    break;

                case 'process':
                    const loadingIcon = msg.loading ? '<span class="loading-spinner"></span>' : '';
                    htmlContent = `
                        <div class="log-entry log-process">
                            <div class="log-header">
                                <span class="log-icon material-icons">${icon}</span>
                                <span class="log-label">Processing</span>
                                <span class="log-time">${timestamp}</span>
                            </div>
                            <div class="log-content">
                                ${loadingIcon}
                                <span>${processContent(msg.text, msg.isHtml)}</span>
                            </div>
                        </div>`;
                    break;

                case 'success':
                    htmlContent = `
                        <div class="log-entry log-success">
                            <div class="log-header">
                                <span class="log-icon material-icons">${icon}</span>
                                <span class="log-label">Success</span>
                                <span class="log-time">${timestamp}</span>
                            </div>
                            <div class="log-content">${processContent(msg.text, msg.isHtml)}</div>
                        </div>`;
                    break;

                case 'system':
                    const systemSeverity = msg.severity || '';
                    const systemClass = systemSeverity ? ` ${systemSeverity}` : '';
                    htmlContent = `
                        <div class="log-entry log-system${systemClass}">
                            ${processContent(msg.text, msg.isHtml)}
                        </div>`;
                    break;

                case 'error':
                    const severity = msg.severity || 'normal';
                    htmlContent = `
                        <div class="log-entry log-error severity-${severity}">
                            <div class="log-header">
                                <span class="log-icon material-icons">${icon}</span>
                                <span class="log-label">Error</span>
                                <span class="log-time">${timestamp}</span>
                            </div>
                            <div class="log-content">${processContent(msg.text, msg.isHtml)}</div>
                        </div>`;
                    break;

                case 'removeLastMsg':
                    this.removeLastDialogContent();
                    break;

                default:
                    htmlContent = `
                        <div class="log-entry log-default">
                            <div class="log-content">${processContent(msg.text, msg.isHtml)}</div>
                        </div>`;
            }

            // Add the current message
            this.addDialogContent(htmlContent);
            this.scrollToBottom();

            // Add click handlers for collapsible function calls
            setTimeout(() => {
                this.setupCollapseHandlers();
            }, 10);
        });


        this.aiService.createImage$.subscribe((imageMessage: any) => {
            // Store image data and show inline options
            this.currentImageData = imageMessage;
            this.showImageOptions = true;

            // Add image to chat log with options
            const imageUrl = imageMessage.imageBase64
                ? `data:image/png;base64,${imageMessage.imageBase64}`
                : imageMessage.imageUrl;

            const sceneId = imageMessage.sceneSlice.sceneId;
            const itemId = imageMessage.sceneSlice.sceneItem.$.id;
            const playerId = imageMessage.sceneSlice.sceneItem.$.player;

            let optionsHtml = `
                <div class="ai-image-result">
                    <div class="ai-image-info">
                        <span><strong>Title:</strong> ${imageMessage.functionArgs.title}</span>
                        <span><strong>Size:</strong> ${imageMessage.functionArgs.size}</span>
                    </div>
                    <div class="ai-image-preview">
                        <img src="${imageUrl}" alt="Generated image" />
                    </div>
                    <div class="ai-image-options">
                        <div class="option-title">Select an action:</div>
                        <div class="option-item" data-option="none">1. Download only</div>
                        <div class="option-item" data-option="addToScene">2. Download and add to the scene</div>`;

            let optionNumber = 3;
            if (sceneId != itemId) {
                if (playerId === '3130') {
                    optionsHtml += `<div class="option-item" data-option="replaceSelectedResource">${optionNumber}. Download and replace the selected Item</div>`;
                    optionNumber++;
                }
                if (playerId === '4102') {
                    optionsHtml += `<div class="option-item" data-option="addToSelectedPlaylist">${optionNumber}. Download and add to the Playlist</div>`;
                    optionNumber++;

                    if (imageMessage.selectedPlaylistIndexes?.length == 1) {
                        optionsHtml += `<div class="option-item" data-option="replaceSelectedPlaylistItem">${optionNumber}. Download and replace the selected item in the Playlist</div>`;
                    }
                }
            }

            optionsHtml += `
                    </div>
                </div>`;

            this.addDialogContent(optionsHtml);
            this.scrollToBottom();

            // Setup click handlers for options
            setTimeout(() => {
                this.setupImageOptionHandlers();
            }, 10);
        });


        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (SpeechRecognition) {
            this.recognition = new SpeechRecognition();
            this.recognition.continuous = true;
            this.recognition.lang = 'en-US';

            this.recognition.onresult = (event: any) => {
                const last = event.results.length - 1;
                this.transcript += event.results[last][0].transcript + ' ';

                this.prompt = this.transcript;
            };

            this.recognition.onend = () => {
                this.isListening = false;
            };

            this.recognition.onerror = (event: any) => {
                console.error('Speech recognition error', event.error);
                this.isListening = false;
            };
        } else {
            alert('Speech recognition is not supported in this browser.');
        }
    }


    startResize(event: MouseEvent, direction: 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right') {
        event.preventDefault();
        this.resizing = true;
        this.resizeDirection = direction;

        const startX = event.clientX;
        const startY = event.clientY;
        const startWidth = this.dialogState.width;
        const startHeight = this.dialogState.height;
        const startTop = this.dialogState.top;
        const startLeft = this.dialogState.left;

        const onMouseMove = (moveEvent: MouseEvent) => {
            if (!this.resizing) return;

            const dx = moveEvent.clientX - startX;
            const dy = moveEvent.clientY - startY;

            switch (this.resizeDirection) {
                case 'right':
                    this.dialogState.width = Math.max(this.MIN_WIDTH, startWidth + dx);
                    break;
                case 'left':
                    const newWidthLeft = Math.max(this.MIN_WIDTH, startWidth - dx);
                    const deltaLeft = startWidth - newWidthLeft;
                    this.dialogState.width = newWidthLeft;
                    this.dialogState.left = startLeft + deltaLeft;
                    break;
                case 'bottom':
                    this.dialogState.height = Math.max(this.MIN_HEIGHT, startHeight + dy);
                    break;
                case 'top':
                    const newHeightTop = Math.max(this.MIN_HEIGHT, startHeight - dy);
                    const deltaTop = startHeight - newHeightTop;
                    this.dialogState.height = newHeightTop;
                    this.dialogState.top = startTop + deltaTop;
                    break;
                case 'top-left':
                    const newWidthTL = Math.max(this.MIN_WIDTH, startWidth - dx);
                    const newHeightTL = Math.max(this.MIN_HEIGHT, startHeight - dy);
                    const deltaTLLeft = startWidth - newWidthTL;
                    const deltaTLTop = startHeight - newHeightTL;
                    this.dialogState.width = newWidthTL;
                    this.dialogState.height = newHeightTL;
                    this.dialogState.left = startLeft + deltaTLLeft;
                    this.dialogState.top = startTop + deltaTLTop;
                    break;
                case 'top-right':
                    this.dialogState.width = Math.max(this.MIN_WIDTH, startWidth + dx);
                    const newHeightTR = Math.max(this.MIN_HEIGHT, startHeight - dy);
                    const deltaTRTop = startHeight - newHeightTR;
                    this.dialogState.height = newHeightTR;
                    this.dialogState.top = startTop + deltaTRTop;
                    break;
                case 'bottom-left':
                    const newWidthBL = Math.max(this.MIN_WIDTH, startWidth - dx);
                    const deltaBLLeft = startWidth - newWidthBL;
                    this.dialogState.width = newWidthBL;
                    this.dialogState.height = Math.max(this.MIN_HEIGHT, startHeight + dy);
                    this.dialogState.left = startLeft + deltaBLLeft;
                    break;
                case 'bottom-right':
                    this.dialogState.width = Math.max(this.MIN_WIDTH, startWidth + dx);
                    this.dialogState.height = Math.max(this.MIN_HEIGHT, startHeight + dy);
                    break;
            }
        };

        const onMouseUp = () => {
            this.resizing = false;
            this.saveDialogSize();  // Save size after resize
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    }


    @timeout(10)
    scrollToBottom() {
        const el = this.textArea.nativeElement;
        el.scrollTop = el.scrollHeight;
    }

    setupCollapseHandlers() {
        if (!this.textArea) return;

        const toggles = this.textArea.nativeElement.querySelectorAll('.collapse-toggle');
        toggles.forEach((toggle: HTMLElement) => {
            // Remove any existing event listeners
            const newToggle = toggle.cloneNode(true) as HTMLElement;
            toggle.parentNode?.replaceChild(newToggle, toggle);

            // Add new event listener
            newToggle.addEventListener('click', () => {
                const functionId = newToggle.getAttribute('data-function-id');
                if (functionId) {
                    const container = document.getElementById(functionId);
                    const arrow = newToggle.querySelector('.arrow');

                    if (container) {
                        container.classList.toggle('collapsed');
                        arrow?.classList.toggle('expanded');
                    }
                }
            });
        });
    }

    setupImageOptionHandlers() {
        if (!this.textArea) return;

        const optionItems = this.textArea.nativeElement.querySelectorAll('.option-item');
        optionItems.forEach((item: HTMLElement) => {
            // Remove any existing event listeners by cloning
            const newItem = item.cloneNode(true) as HTMLElement;
            item.parentNode?.replaceChild(newItem, item);

            // Add new event listener
            newItem.addEventListener('click', () => {
                this.executeImageOption(newItem, optionItems);
            });
        });

        // Add keyboard listener for number keys
        this.setupImageKeyboardHandler(optionItems);
    }

    setupImageKeyboardHandler(optionItems: NodeListOf<Element>) {
        const keyHandler = (event: KeyboardEvent) => {
            if (!this.showImageOptions || !this.currentImageData) return;

            const key = event.key;
            const optionNumber = parseInt(key);

            // Check if a number key was pressed (1-9)
            if (optionNumber >= 1 && optionNumber <= 9) {
                const index = optionNumber - 1;
                if (index < optionItems.length) {
                    event.preventDefault();
                    const selectedItem = optionItems[index] as HTMLElement;
                    this.executeImageOption(selectedItem, optionItems);
                }
            }
        };

        // Remove existing listener if any
        document.removeEventListener('keydown', keyHandler);
        // Add new listener
        document.addEventListener('keydown', keyHandler);
    }

    executeImageOption(selectedItem: HTMLElement, allItems: NodeListOf<Element>) {
        const option = selectedItem.getAttribute('data-option');
        if (option && this.currentImageData) {
            // Mark selected option visually
            allItems.forEach(opt => opt.classList.remove('selected'));
            selectedItem.classList.add('selected');

            // Execute the download with selected option
            this.currentImageData.option = option;
            this.aiService.downloadImageCompleted(this.currentImageData).subscribe(() => {
                // Clear the current image data
                this.currentImageData = null;
                this.showImageOptions = false;

                // Show success message
                const successMsg = {
                    type: 'success',
                    text: `Image ${option === 'none' ? 'downloaded' : 'downloaded and ' + option.replace(/([A-Z])/g, ' $1').toLowerCase()}`,
                    timestamp: new Date().toISOString(),
                    icon: 'check_circle'
                };
                this.aiService.log$.next(successMsg);
            });
        }
    }

    toggleListening() {
        if (!this.recognition) return;

        if (this.isListening) {
            this.recognition.stop();
            this.isListening = false;
        } else {
            this.transcript = '';
            this.recognition.start();
            this.isListening = true;
        }
    }


    onSend(): void {
        this.clearDialogContent();
        this.scrollToBottom();


        this.aiService.send(this.aiSettings.chat, this.prompt).subscribe(() => {
            this.prompt = "";
            // Focus will be set automatically when busy becomes false
        });
    }

    onStop(): void {
        this.aiService.stopCurrentRequest();
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent): void {
        if (this.busy && this.showDialog) {
            event.preventDefault();
            event.stopPropagation();
            this.onStop();
        }
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        if (this.showDialog) {
            this.constrainPosition();
        }
    }

    private saveDialogSize(): void {
        const sizeData = {
            width: this.dialogState.width,
            height: this.dialogState.height,
            top: this.dialogState.top,
            left: this.dialogState.left
        };
        localStorage.setItem(this.DIALOG_SIZE_KEY, JSON.stringify(sizeData));
    }

    private loadDialogSize(): void {
        const savedSize = localStorage.getItem(this.DIALOG_SIZE_KEY);
        if (savedSize) {
            try {
                const sizeData = JSON.parse(savedSize);
                // Validate and apply saved size
                if (sizeData.width && sizeData.width > 300 && sizeData.width < window.innerWidth) {
                    this.dialogState.width = sizeData.width;
                }
                if (sizeData.height && sizeData.height > 200 && sizeData.height < window.innerHeight) {
                    this.dialogState.height = sizeData.height;
                }
                if (sizeData.top && sizeData.top >= 0 && sizeData.top < window.innerHeight - 100) {
                    this.dialogState.top = sizeData.top;
                }
                if (sizeData.left && sizeData.left >= 0 && sizeData.left < window.innerWidth - 100) {
                    this.dialogState.left = sizeData.left;
                }
            } catch (e) {
                console.error('Failed to load saved dialog size:', e);
            }
        }
    }

    onDragEnded(event: CdkDragEnd) {
        const element = event.source.element.nativeElement as HTMLElement;
        const transform = getComputedStyle(element).transform;

        if (transform && transform !== 'none') {
            const matrix = new DOMMatrixReadOnly(transform);

            // m41 = translateX, m42 = translateY
            this.dialogState.left += matrix.m41;
            this.dialogState.top += matrix.m42;

            // Constrain position to stay within window boundaries
            this.constrainPosition();

            // Save position after drag
            this.saveDialogSize();

            // Reset transform so styles don't stack
            element.style.transform = 'none';

            // Apply updated position directly to the element to prevent visual jump
            element.style.left = this.dialogState.left + 'px';
            element.style.top = this.dialogState.top + 'px';

            event.source._dragRef.reset();
        }
    }

    private constrainPosition(): void {
        const maxLeft = window.innerWidth - this.dialogState.width;
        const maxTop = window.innerHeight - this.dialogState.height;

        this.dialogState.left = Math.max(0, Math.min(this.dialogState.left, maxLeft));
        this.dialogState.top = Math.max(0, Math.min(this.dialogState.top, maxTop));
    }

    hideDialog() {
        this.updateDialogState();
        this.aiService.showDialog(false);
    }

    settings() {
        const dialogRef = this.dialog.open(AiSettingsDialogComponent, {
            width: '600px',
            maxWidth: '90vw',
            data: this.aiSettings,
            panelClass: 'ai-settings-dialog-above',
            hasBackdrop: true,
            backdropClass: 'ai-settings-backdrop'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // Settings were saved, update local reference
                this.aiSettings = result;
                this.title = this.aiService.aiApis.chat[this.aiSettings.chat].label;
            }
        });
    }

    addMoney() {
        const dialogRef = this.dialog.open(AddAiCreditComponent, {
            width: '450px',
            maxWidth: '90vw',
            data: this.balance
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result !== undefined) {
                this.aiBalanceService.addCredit(result).subscribe(ret => {
                    console.log('Credit added successfully');
                });
            }
        });
    }


    saveJson() {
        localStorage['savedTestScene'] = JSON.stringify(this.aiService.scenePlayerData);
    }



    showJson(sourceJson, destJson) {
        const saved = JSON.parse(localStorage['savedTestScene']);
        if (sourceJson == 'saved') {
            sourceJson = saved;
        }
        if (destJson == 'saved') {
            destJson = saved;
        }

        const dialogRef = this.dialog.open(JsonDiffDialogComponent, {
            data: {
                leftJson: sourceJson,
                rightJson: destJson
            },
            width: '90vw',
            maxWidth: '1400px'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result === true) {
                // User clicked OK - apply the destination JSON
                this.aiService.scenePlayerData = destJson;

                this.storeSelector.dispatch(actionUpdateScenePlayer({
                    sceneId: this.aiService.selectedSceneId,
                    playerData: this.aiService.newSceneData
                }));

                this.storeSelector.dispatch(actionCRUDScene({
                    data: {
                        command: 'rebuild',
                        sceneId: this.aiService.selectedSceneId,
                        itemIds: []
                    }
                }));

            }
        });
    }

    cleanView() {
        this.clearDialogContent();
        this.aiService.clearConversationHistory();
    }

    updateDialogState() {
        if (this.aiDialog != null) {
            const rect = this.aiDialog.nativeElement.getBoundingClientRect();
            this.dialogState = {
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height
            };
            console.log('updateDialogState');
        }
    }

    escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    addDialogContent(htmlContent: string): void {
        this.dialogContentArray.push(htmlContent);
        this.rebuildDialogHtml();
    }

    removeLastDialogContent(): void {
        this.dialogContentArray.pop();
        this.rebuildDialogHtml();
    }

    clearDialogContent(): void {
        this.dialogContentArray = [];
        this.dialogHtml = '';
    }

    rebuildDialogHtml(): void {
        this.dialogHtml = this.dialogContentArray.join('');
    }
}
